import variants from "../photo-variants.json";

type VariantEntry = { widths: number[]; fullWidth: number };

const MANIFEST = variants as Record<string, VariantEntry>;

export type ResponsiveImageProps = {
  src: string;
  srcSet?: string;
  sizes?: string;
};

/**
 * Build responsive-image attributes for a property photo.
 *
 * Given a served photo path (e.g. `/photos/foo.webp`) it returns the original
 * `src` (used as the no-`srcset` fallback) plus a `srcSet` listing the
 * pre-generated WebP width variants recorded in `photo-variants.json`. Pass a
 * `sizes` string describing the image's rendered width at each breakpoint so the
 * browser can pick the smallest sufficient variant for the viewport and DPR.
 *
 * Photos without generated variants (e.g. remote URLs) fall back to just `src`.
 */
export function responsiveImage(
  src: string,
  sizes?: string,
): ResponsiveImageProps {
  const entry = MANIFEST[src];
  if (!entry || entry.widths.length === 0) {
    return sizes ? { src, sizes } : { src };
  }

  const base = src.replace(/\.webp$/, "");
  // Generated variants point at the `-<w>.webp` siblings; the largest candidate
  // is the original (unsuffixed) served file at its full width.
  const candidates = entry.widths
    .filter((w) => w < entry.fullWidth)
    .map((w) => `${base}-${w}.webp ${w}w`);
  candidates.push(`${src} ${entry.fullWidth}w`);
  const srcSet = candidates.join(", ");

  return { src, srcSet, ...(sizes ? { sizes } : {}) };
}
